import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { UserContext } from "./UserProvider";
import AppConfig from "../config/envConfig";
import _ from "lodash";
import { interestBodyResponses } from "@workaday-sdk/client-api/lib/events/websockets/overWebsocket/interest";
import { WorkadayEventTypes } from "@workaday-sdk/client-api/lib/logic/app/publishEvent";
import {
  WebsocketResponseMessage,
  WebsocketResponseMessageSchema,
} from "@workaday-sdk/client-api/lib/events/websockets/overWebsocket/base";

export interface WebsocketProviderProps {
  children: ReactNode;
}
export interface WebsocketProviderContextInterface {
  registerEventCallback: (
    fn: (_d: WebsocketResponseMessage) => void,
    scope: string,
    callbackName: string
  ) => void;
  removeEventCallback: (scope: string, callbackName: string) => void;
  pushNotification: (data: object) => void;
  connected: boolean;
}

export const WebsocketContext =
  React.createContext<WebsocketProviderContextInterface>({
    registerEventCallback: (
      _fn: (evt: WebsocketResponseMessage) => void,
      _scope: string,
      _callbackName: string
    ) => {},
    removeEventCallback: (_scope: string, _callbackName: string) => {},
    pushNotification: (_data: object) => {},
    connected: false,
  });

type CallbackRegistry = Record<
  string,
  Record<string, { callback: (_evt: WebsocketResponseMessage) => void }>
>;

const runRegistryCallbackFunctions = (
  registry: CallbackRegistry,
  scope: string,
  data: WebsocketResponseMessage
) => {
  if (registry[scope]) {
    const callbackRegistry = registry[scope];
    const pathKeys = _.keys(registry[scope]);
    pathKeys.forEach((key) => {
      callbackRegistry[key].callback(data);
    });
  }
};

const WebsocketProvider = ({
  children,
}: WebsocketProviderProps): JSX.Element => {
  const { Provider } = WebsocketContext;
  const { userSession } = useContext(UserContext);
  const pingTimerRef = useRef<number | undefined>(undefined); // send a ping message into the websocket every X minutes must be < 10 minutes
  const [pageInterests, setPageInterests] = useState<Record<string, any>>({});
  const [previousJwt, setPreviousJwt] = useState<string>("");
  const [pendingMessages, setPendingMessages] = useState<Array<object>>([]);
  const [registeredListeners] = useState<CallbackRegistry>({});
  const [wsConnection, setWSConnection] = useState<WebSocket | undefined>(
    undefined
  );
  const [isConnected, setIsConnected] = useState(false)
  const [wsClosed, setWsClosed] = useState(false);
  const location = useLocation();
  const { pathname } = location;

  const _send = (ws: WebSocket, data: object) => {
    if (ws && ws.readyState === ws.OPEN) {
      ws.send(JSON.stringify(data));
    } else {
      const newPendingMessages = pendingMessages.slice();
      newPendingMessages.push(data);
      setPendingMessages(newPendingMessages);
    }
  };

  const handleWSReceivedMessage = (evt: MessageEvent<any>) => {
    if (evt?.data) {
      try {
        const message = WebsocketResponseMessageSchema.parse(
          JSON.parse(evt.data)
        );
        runRegistryCallbackFunctions(registeredListeners, "*", message);
        switch (message.scope) {
          case "PingPong":
            break;
          case "Interest":
            const interestEventResponse = interestBodyResponses.parse(
              message.body
            );
            switch (interestEventResponse.method) {
              case "added":
                const updateInterestPageList = { ...pageInterests };
                _.set(
                  updateInterestPageList,
                  `${pathname}.${interestEventResponse.interest.focus}`,
                  interestEventResponse.interest
                );
                // might change this to be a quiet update keeping the same memory address
                setPageInterests(updateInterestPageList);
                break;
              default:
                break;
            }
            break;
          default:
            runRegistryCallbackFunctions(
              registeredListeners,
              message.scope,
              message
            );
            break;
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const registerEventCallback = useCallback(
    (
      fn: (_d: WebsocketResponseMessage) => void,
      scope: string,
      callbackName: string
    ) => {
      _.set(registeredListeners, `${scope}.${callbackName}`, { callback: fn });
    },
    [registeredListeners]
  );

  const removeEventCallback = useCallback(
    (scope: string, callbackName: string) => {
      if (
        registeredListeners[scope] &&
        registeredListeners[scope][callbackName]
      ) {
        delete registeredListeners[scope][callbackName];
        if (_.keys(registeredListeners[scope]).length === 0) {
          delete registeredListeners[scope];
        }
      }
    },
    [registeredListeners]
  );

  const registerInterest = (interest: string, ws: WebSocket) => {
    if (ws) {
      ws.onmessage = handleWSReceivedMessage;
      _send(ws, {
        action: "interest",
        method: "add",
        body: { focus: interest },
      });
    }
  };

  const removeInterest = (interestId: string, ws: WebSocket) => {
    if (ws) {
      ws.onmessage = handleWSReceivedMessage;
      _send(ws, {
        action: "interest",
        method: "remove",
        body: { id: interestId },
      });
    }
  };

  const sendFunction = useCallback(
    (data: object) => {
      if (wsConnection) {
        _send(wsConnection, data);
      }
    },
    // eslint-disable-next-line
    [wsConnection]
  );

  useEffect(()=>{
    if (wsConnection && wsConnection.readyState === wsConnection.OPEN) {
      setIsConnected(true)
    } else {
      setIsConnected(false)
    }
  },[wsConnection, wsConnection?.readyState, setIsConnected])

  const registerPathInterests = (ws: WebSocket, pathname: string) => {
    // Add URL path patters here to register page wide interest(s)
    switch (true) {
      // match on job detail path
      case /^\/user\/account\/job\/([A-Za-z0-9_-]+)\/details$/.test(pathname):
        const match = pathname.match(
          /^\/user\/account\/job\/([A-Za-z0-9_-]+)\/details$/
        );
        if (match?.[1]) {
          registerInterest(`Job#${match[1]}`, ws);
        }
        break;
      case /^\/user\/account\/jobs\/assigned$/.test(pathname):
      case /^\/user\/account\/jobs\/open$/.test(pathname):
        registerInterest(WorkadayEventTypes.JobCanceled, ws);
        break;
      case /^\/$/.test(pathname):
        registerInterest(WorkadayEventTypes.JobCreated, ws);
        break;
      default:
        // No interest for this page/route
        break;
    }
  };

  const sendPingEvent = useCallback(() => {
    try {
      if (wsConnection && wsConnection?.OPEN === wsConnection?.readyState) {
        _send(wsConnection, { action: "ping" });
      }
    } catch (_e) {
      //cleanup our ping interval
      if (pingTimerRef.current) {
        window.clearInterval(pingTimerRef.current);
        pingTimerRef.current = undefined;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsConnection, pingTimerRef]);

  const establishWebsocketConnection = useCallback(
    (jwt: string) => {
      const ws = new WebSocket(AppConfig.websocketUri, [
        "WorkadayWSProtocol",
        jwt.substring(7),
      ]);
      ws.onerror = () => {
        //TODO; figure out what to do on error states
      };
      ws.onmessage = handleWSReceivedMessage;
      ws.onopen = () => {
        setPageInterests({});
        setWSConnection(ws);
      };
      ws.onclose = () => {
        setWsClosed(true);
      };
      return ws;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setPageInterests, setWSConnection, setWsClosed]
  );

  const connectWebsocket = useMemo(() => {
    if (userSession?.jwt) return _.debounce(establishWebsocketConnection, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession]);

  useEffect(() => {
    if (connectWebsocket && previousJwt !== userSession?.jwt) {
      if (wsConnection && wsConnection?.readyState === wsConnection?.OPEN) {
        wsConnection?.close();
      } else {
        connectWebsocket?.(userSession?.jwt || "");
      }
      setPreviousJwt(userSession?.jwt || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectWebsocket, previousJwt, wsConnection]);

  useEffect(() => {
    if (wsClosed) {
      if (wsConnection?.readyState === wsConnection?.CLOSED) {
        connectWebsocket?.(userSession?.jwt || "");
      }
      setWsClosed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsClosed, setWsClosed, wsConnection]);

  useEffect(() => {
    if (pathname && wsConnection) {
      // remove all other paths other than global
      if (pageInterests) {
        const keys = _.keys(pageInterests);
        keys.forEach((key) => {
          if (key !== "global") {
            const interestKeys = _.keys(pageInterests[key]);
            interestKeys.forEach((interestKey) => {
              removeInterest(pageInterests[key][interestKey].id, wsConnection);
              delete pageInterests[key][interestKey];
            });
            delete pageInterests[key];
          }
        });
      }
      // Add URL path patters here to register page wide interest(s)
      registerPathInterests(wsConnection, pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, wsConnection]);

  useEffect(() => {
    if (wsConnection) {
      if (pingTimerRef.current) {
        window.clearInterval(pingTimerRef.current);
        pingTimerRef.current = undefined;
      }
      pingTimerRef.current = window.setInterval(
        sendPingEvent,
        AppConfig.websocketPingTimer
      );
    }
  }, [pingTimerRef, wsConnection, sendPingEvent]);

  useEffect(() => {
    return () => {
      if (pingTimerRef.current) {
        window.clearInterval(pingTimerRef.current);
        pingTimerRef.current = undefined;
      }
    };
  }, []);

  return (
    <Provider
      value={{
        registerEventCallback,
        removeEventCallback,
        pushNotification: sendFunction,
        connected: isConnected
      }}
    >
      {children}
    </Provider>
  );
};

export default WebsocketProvider;
